import {
  ChangeEventHandler,
  forwardRef,
  useCallback,
  useEffect,
  useState
} from 'react';
import { SelectFieldProps, SelectOptionProp } from './types';
import clsx from 'clsx';
import Field from '../Field';

/**
 *  UI Component
 */
const SelectField = forwardRef<HTMLDivElement, SelectFieldProps>(
  function SelectField(props, ref) {
    const {
      className,
      value,
      defaultValue,
      name,
      placeholder,
      headeroptions,
      onChange,
      onFocus,
      onBlur,
      selectRef,
      selectProps,
      disabled,
      options: optionsProp,
      optGroup,
      optionsResolver,
      handleSelected,
      refreshOptions,
      ...rest
    } = props;

    const [options, setOptions] = useState<SelectOptionProp[] | null>([]);
    const [focused, setFocused] = useState(false);
    const [fetching, setFetching] = useState<boolean>(!!optionsResolver); // Initialize with 'false'

    const handleChange = useCallback<ChangeEventHandler<HTMLSelectElement>>(
      (e) => {
        if (onChange) {
          const opt = options?.find(
            (o) => `${o.value}` === `${e.target.value}`
          );
          onChange(e.target.value, opt || undefined);
          handleSelected &&
            opt &&
            handleSelected([{ value: opt.value, label: opt.label }]);
        }
      },
      [onChange, options]
    );

    const handleFocus = useCallback(() => {
      setFocused(true);
      if (onFocus) onFocus();
    }, [setFocused, onFocus]);

    const handleBlur = useCallback(() => {
      setFocused(false);
      if (onBlur) onBlur();
    }, [setFocused, onBlur]);

    const fetchOptions = () => {
      setFetching(true);
      if (!optionsResolver) return;
      optionsResolver()
        .then((data) => {
          setOptions(data);
        })
        .finally(() => {
          setFetching(false);
        });
    };

    useEffect(() => {
      setOptions(optionsProp || []);
    }, [optionsProp]);

    useEffect(() => {
      if (optionsResolver) {
        fetchOptions();
      }
    }, [refreshOptions]);

    const cls = styleSelectField(props);

    const inputElProps = {
      name,
      placeholder,
      headeroptions,
      value,
      defaultValue,
      className: clsx(cls.input),
      onChange: handleChange,
      onFocus: handleFocus,
      onBlur: handleBlur,
      disabled: disabled
    };

    return (
      <Field
        className={clsx(cls.main, className)}
        focused={focused}
        ref={ref}
        {...rest}
      >
        <select
          ref={selectRef}
          {...inputElProps}
          {...selectProps}
          disabled={disabled}
        >
          {placeholder && (
            <option value="">{fetching ? 'Loading...' : placeholder}</option>
          )}
          {headeroptions &&
            headeroptions.map((option) => (
              <option
                value={option.value}
                key={`${option.value}`}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}
          {options?.map((option) => (
            <option value={option.value} key={`${option.key || option.value}`}>
              {focused ? <>&nbsp;&nbsp;&nbsp;</> : ''}
              {option.label}
            </option>
          ))}
          {optGroup && (
            <optgroup label={optGroup.label}>
              {optGroup?.options.map((option) => (
                <option
                  value={option.value}
                  key={`${option.value}`}
                  disabled={option.disabled}
                >
                  {focused ? <>&nbsp;&nbsp;&nbsp;</> : ''}
                  {option.label}
                </option>
              ))}
            </optgroup>
          )}
        </select>
      </Field>
    );
  }
);

export default SelectField;

/**
 * Assign classNames to component based on properties
 */
function styleSelectField(props: SelectFieldProps) {
  const mc = 'SelectField';
  const cls = {
    main: [mc],
    input: ['Field__Input', `${mc}__Input`]
  };
  if (props.value !== undefined && props.value !== null && props.value !== '') {
    cls.main.push(`${mc}--isFilled`);
  }
  if (props.appearance) {
    cls.main.push(`${mc}--${props.appearance}`);
  }
  return cls;
}
